import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { Entry } from '../../../features/admin/modules/modules.model';

/**
 * Types for `readonly-onboarding.component.ts` (C6 — previously declared inline in the component
 * file, moved out per the project convention of keeping types in their own `«entity».types.ts`).
 */

export interface AnswerRow {
  label: string;
  value: string;
  fullWidth: boolean;
}

export interface AnswerSection {
  label: string;
  /** Review-flag target for this wizard page — `pageTarget(key)`. */
  target: string;
  rows: AnswerRow[];
}

export interface ProcessedPage {
  label: string;
  fields: FormlyFieldConfig[];
  key?: FormlyFieldConfig['key'];
  props?: FormlyFieldConfig['props'];
  className?: FormlyFieldConfig['className'];
  hide?: FormlyFieldConfig['hide'];
  /**
   * Props of the generatedForm-root wrapper field (the wizard/tabs/pages/group container one
   * level above this page) — undefined when this page IS that root-level field (single-page
   * form, no separate wrapper above it). Field-rights are a real admin-configurable surface on
   * that wrapper too (wizard/pages/group/tabs all keep the "Veldrechten per gebruikersrol"
   * toggle — form-builder.component.ts hideRoleBasedRights only hides it for `datatype: 'array'`
   * types), so it must enter the ancestor chain the same way FormService.addExpressionsAndHooks
   * accumulates ancestorProps from the true generatedForm root, not just from this page down.
   */
  wrapperProps?: FormlyFieldConfig['props'];
}

/**
 * One review write, from the moment it is accepted until its response lands. Every write goes
 * through the same queue (see `#persist`): the store row is rebuilt from whatever the RESPONSE
 * carries, so two requests in flight at once leave the row holding the value whose response came
 * back last rather than the value that was written last.
 *
 * Unexported-in-spirit: it is `#persist`'s bookkeeping, not a shape anything outside this
 * component is meant to hold — exported only because it now lives in its own file.
 */
export interface PendingReviewWrite {
  moduleId: string;
  entryId: string;
  fieldKey: string;
  /** The aanmelding this write started from — the fallback row when the store has none. */
  aanmelding: Entry;
  /** This field's own sub-keys to write. */
  ownValue: Record<string, unknown>;
  /**
   * This write's rollback baseline for its own sub-keys — what the row reverts to if this write
   * fails. Captured at enqueue time, then kept current while it waits: whenever an earlier write
   * ahead of it in the queue settles, `#propagateSettledKeys` refreshes this for every key the two
   * writes share, so it always reflects what the row holds for that key right now rather than a
   * stale snapshot from before the earlier write's own outcome landed. A key nothing ahead of it
   * ever touches keeps its original enqueue-time value — nothing changed it.
   */
  previous: Record<string, unknown>;
  clearsLocalFlags: boolean;
  onSettled?: (succeeded: boolean) => void;
  /** Whether this write waited its turn — a queued one re-mirrors before it goes out. */
  queued: boolean;
  /** A newer flag write replaced this one while it waited; it must not be sent at all. */
  superseded: boolean;
  /**
   * Whether a NEWER flag write may drop this one outright instead of waiting its turn. Declared by
   * the caller that created the write, not inferred from its shape: `#persistFlags`'s writes are
   * interchangeable snapshots of the same flag list, so only the newest one needs to reach the
   * server — but the resubmit-resolve write carries `RESUBMITTED_SUB_KEY` on top, a key no later
   * ordinary flag write ever repeats, so dropping it would silently strand that marker on the
   * server forever.
   */
  replaceable: boolean;
}

export interface ApplicantField {
  label: string;
  value: string | null;
}

/**
 * Tenant vocabulary used to lock the review after it leaves the active review state. Promote to a
 * builder prop only once a second tenant needs a different value.
 */
export const ONBOARDING_STATUS_SLUGS = { inReview: 'in-behandeling' } as const;
